import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { usePublicPathLocale } from '../../../hooks/usePublicPathLocale';
import { SUPPORTED_LOCALES } from '../../../i18n/config';
import {
  LLMS_FULL_TXT_URL,
  LLMS_TXT_URL,
  SITE_NAME,
  SITE_URL,
  SITEMAP_LASTMOD,
  SITEMAP_URL,
} from '../../../constants/siteSeo';
import { keisukePublicPath } from '../../../utils/keisukePath';

const HREFLANG_MAP: Record<string, string> = {
  'zh-CN': 'zh-Hans',
};

/** 公開計助向け JSON-LD + hreflang（ja は /keisuke、他は /{locale}/keisuke） */
export default function KeisukePageStructuredData() {
  const { t } = useTranslation();
  const pathLocale = usePublicPathLocale();
  const pageUrl = `${SITE_URL}${keisukePublicPath(pathLocale)}`;
  const description = t('home.feature_keisuke_description');
  const name = t('keisuke.title', { defaultValue: 'Keisuke' });

  const jsonLd = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'WebPage',
        '@id': `${pageUrl}#webpage`,
        url: pageUrl,
        name,
        description,
        dateModified: SITEMAP_LASTMOD,
        isPartOf: { '@id': `${SITE_URL}/#website` },
        about: { '@id': `${SITE_URL}/keisuke#software` },
        mainEntity: { '@id': `${SITE_URL}/keisuke#software` },
      },
      {
        '@type': 'SoftwareApplication',
        '@id': `${SITE_URL}/keisuke#software`,
        name,
        alternateName: ['Keisuke', '計助', 'けいすけ'],
        url: `${SITE_URL}/keisuke`,
        applicationCategory: 'EducationalApplication',
        operatingSystem: 'Web',
        description,
        isAccessibleForFree: true,
        offers: {
          '@type': 'Offer',
          price: '0',
          priceCurrency: 'JPY',
        },
        author: { '@id': `${SITE_URL}/#developer` },
        publisher: { '@id': `${SITE_URL}/#developer` },
      },
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          {
            '@type': 'ListItem',
            position: 1,
            name: SITE_NAME,
            item: SITE_URL,
          },
          {
            '@type': 'ListItem',
            position: 2,
            name,
            item: pageUrl,
          },
        ],
      },
    ],
  };

  return (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      <link rel="describedby" type="text/plain" href={LLMS_FULL_TXT_URL} />
      <link rel="alternate" type="text/plain" href={LLMS_TXT_URL} title="LLMs.txt" />
      <link rel="sitemap" type="application/xml" href={SITEMAP_URL} />
      {SUPPORTED_LOCALES.map((loc) => (
        <link
          key={loc}
          rel="alternate"
          hrefLang={HREFLANG_MAP[loc] ?? loc}
          href={`${SITE_URL}${keisukePublicPath(loc)}`}
        />
      ))}
      <link rel="alternate" hrefLang="x-default" href={`${SITE_URL}/keisuke`} />
    </Helmet>
  );
}
